import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import heroImage from "@/assets/hero-silhouette.png";
import robOrangePortrait from "@/assets/rob-orange-portrait.jpg";
import { SEO } from "@/components/SEO";
import { personSchema } from "@/lib/structuredData";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { Play, BookOpen, Mail, Compass } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="About | Rob Lasa"
        description="I'm Rob. Dad of four boys. Not a therapist. Not a coach. Just a guy who spent years performing niceness until it almost killed him — and finally stopped."
        keywords="Rob Lasa, nice guy syndrome, men's mental health, identity, self-honesty"
        url="/about"
        schema={personSchema}
      />
      <Navigation />
      <Breadcrumbs />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-hero opacity-90" />
        
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-display font-bold text-primary-foreground mb-6">
              I'm Rob. I Was Just Buried.
            </h1>
            <p className="text-xl md:text-2xl text-primary-foreground/90 max-w-2xl mx-auto">
              Dad of four boys. Not a therapist. Not a coach. Not a guru.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            <div>
              <h2 className="text-4xl font-display font-bold text-foreground mb-6">
                The Performance Years
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  I spent years chasing money, vanity, success — thinking providing was enough. 
                  I was there at every dinner, every bedtime, every moment. But I wasn't present. 
                  I was performing.
                </p>
                <p>
                  I was the nice guy. The one who said yes when he meant no. The one who avoided 
                  conflict at any cost. The one who measured his worth by how much he could give 
                  everyone else — and never stopped to ask what he actually needed.
                </p>
                <p className="text-xl font-semibold text-primary italic border-l-4 border-primary pl-6 py-4 bg-muted/30">
                  "My kids had a provider. They didn't have me."
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-4xl font-display font-bold text-foreground mb-6">
                What Was Underneath
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Beneath the surface, I was struggling with addiction, disordered thinking, 
                  and the weight of expectations I couldn't name. I cared so much about what other people thought 
                  that I forgot to ask myself what I thought, who I was, what I actually wanted.
                </p>
                <p>
                  I'm not writing this from the other side of some transformation. 
                  I'm writing it from the middle. I still get overstimulated. I still need alone time. 
                  I still catch myself performing when I'm scared. 
                  This isn't an arrival — it's a practice.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-4xl font-display font-bold text-foreground mb-6">
                What This Is
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  This space exists because I needed it and couldn't find one. 
                  No formulas. No five-step plans. Just honest conversation about what it actually 
                  takes to stop performing and start being present.
                </p>
                <p>
                  I'm documenting what happens when you stop pretending. For my sons. 
                  For any man who reads this and thinks, "That's me."
                </p>
              </div>
            </div>

            {/* What This Is Not */}
            <div className="bg-muted/30 rounded-lg p-8 border-l-4 border-accent">
              <h3 className="text-3xl font-display font-bold text-foreground mb-4">
                What This Is Not
              </h3>
              <div className="space-y-3 text-lg text-muted-foreground">
                <p>This is not a coaching program. I'm not selling a system or a framework.</p>
                <p>This is not a highlight reel. I don't have it figured out.</p>
                <p>This is not self-improvement. It's self-honesty.</p>
                <p className="font-semibold text-foreground pt-2">
                  This is one man's journal. That's it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portrait */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-accent/20 via-accent/10 to-accent/20 rounded-2xl blur-2xl" />
              <img 
                src={robOrangePortrait} 
                alt="Rob Lasa" 
                width="960"
                height="960"
                loading="lazy"
                className="relative w-full h-auto rounded-lg shadow-2xl border border-border/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-hero relative overflow-hidden">
        <div className="container mx-auto px-4 text-center relative z-10 max-w-3xl">
          <p className="text-sm uppercase tracking-[0.3em] text-primary-foreground/70 mb-4">
            Start Here
          </p>
          <h2 className="text-4xl md:text-6xl font-display font-bold text-primary-foreground mb-6 leading-tight">
            Watch the Message
          </h2>
          <p className="text-lg md:text-xl text-primary-foreground/90 mb-10 max-w-2xl mx-auto">
            A short, unscripted video from me — the one I'd want another man to see first.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button asChild size="lg" className="bg-accent text-white hover:bg-accent/90 font-bold text-lg px-8 py-6">
              <Link to="/journal">
                <Play className="mr-2 h-5 w-5" />
                Watch the Message
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 font-semibold">
              <Link to="/journal">
                Read the Journal
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Email Signup */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
              Stay Honest
            </h2>
            <p className="text-lg text-muted-foreground">
              Occasional reflections and journal entries. No spam. No programs. Just honest words from a man in the middle of it.
            </p>
          </div>
          <NewsletterSignup source="other" />
        </div>
      </section>

      {/* Resources & Contact */}
      <section className="py-20 bg-background border-t border-border/40">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-10 text-center">
              Keep Going
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/journal"
                className="group block p-6 rounded-lg border border-border bg-card hover:border-accent transition-colors"
              >
                <BookOpen className="h-6 w-6 text-accent mb-3" />
                <h3 className="text-xl font-display font-bold text-foreground mb-2 group-hover:text-accent transition-colors">
                  The Journal
                </h3>
                <p className="text-muted-foreground text-sm">
                  Written reflections and video entries from the middle of it.
                </p>
              </Link>
              <Link
                to="/nice-guy"
                className="group block p-6 rounded-lg border border-border bg-card hover:border-accent transition-colors"
              >
                <Compass className="h-6 w-6 text-accent mb-3" />
                <h3 className="text-xl font-display font-bold text-foreground mb-2 group-hover:text-accent transition-colors">
                  The Nice Guy Trap
                </h3>
                <p className="text-muted-foreground text-sm">
                  The core story — what being nice almost cost me.
                </p>
              </Link>
              <Link
                to="/connect"
                className="group block p-6 rounded-lg border border-border bg-card hover:border-accent transition-colors"
              >
                <Mail className="h-6 w-6 text-accent mb-3" />
                <h3 className="text-xl font-display font-bold text-foreground mb-2 group-hover:text-accent transition-colors">
                  Get in Touch
                </h3>
                <p className="text-muted-foreground text-sm">
                  Podcasts, media, or just to say something out loud.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
